import {
  monthLabel,
  rangeLabel,
  type RevenueByPerson,
  type RevenueNotCredited,
  type RevenuePage,
  type RevenueTrendMonth,
  type WithheldField,
} from '@servwell/shared';
import { ordinal } from '../../../lib/format';

/**
 * What the Revenue page shows, in its own words and by its own rules, shared by the sections
 * that draw it and by its CSV (`revenueCsv`), so the file cannot name a section, word a month
 * or hold a row differently from the screen. Pure: no React, no session.
 */

/** The page's section headings: the chart's names its basis ("Invoiced by month"). */
export const REVENUE_SECTION = {
  byMonth: (label: string) => `${label} by month`,
  byServiceType: 'By service type',
  byPerson: 'By person',
} as const;

/** How far a month in progress runs, from its last day: "to the 28th". */
export const toThe = (day: string) => `to the ${ordinal(Number(day.slice(8)))}`;

/** A chart month in words: "Aug 2026", or "Sep 2026, to the 28th" in progress. */
export function monthWords(m: RevenueTrendMonth): string {
  return m.partialThrough
    ? `${monthLabel(m.month)}, ${toThe(m.partialThrough)}`
    : monthLabel(m.month);
}

/** The line under the chart's heading: its window, and what each month is beside. */
export function chartWindowWords(to: string): string {
  return `The twelve months to ${rangeLabel(to, to)}, whatever the period, each beside the same days a year earlier.`;
}

/**
 * Whether "Not credited" has a row: when it is not zero, now or a year earlier (README gap
 * ruling 7; the rule totals always show).
 */
export function showsNotCredited(notCredited: RevenueNotCredited): boolean {
  return notCredited.cents !== 0 || notCredited.yearEarlierCents !== 0;
}

/** The server's reasons for withholding by person; another field's are not this section's. */
export function byPersonReasons(withheld: readonly WithheldField[]): WithheldField[] {
  return withheld.filter((w) => w.field === 'byPerson');
}

/**
 * Whether by person is drawn: its people were sent AND nothing says they are withheld. The
 * answer decides, never the caller's role, and an answer that says both fails closed: the
 * notice, and no name.
 */
export function byPersonShown(
  page: RevenuePage,
): page is RevenuePage & { byPerson: RevenueByPerson } {
  return page.byPerson !== null && byPersonReasons(page.withheld).length === 0;
}
